import { seg } from "./api";

/**
 * Payloads behind the public show and DJ pages.
 *
 * These endpoints sit outside the OpenAPI schema on purpose (see
 * `station_public_directory_view`): only this site reads them, so the types
 * are hand-written here rather than generated.
 */

/** Just enough station to head a page and feed the player. */
export interface PublicStation {
  id: string;
  slug: string;
  name: string;
  logo_url: string;
  stream_url: string;
}

export interface DjSummary {
  slug: string;
  name: string;
  image_url: string;
}

export interface ShowSummary {
  slug: string;
  name: string;
  image_url: string;
}

export interface PublicShow extends ShowSummary {
  id: string;
  description: string;
  url: string;
  hosts: DjSummary[];
}

export interface PublicDj extends DjSummary {
  bio: string;
}

export interface Airing {
  start: string;
  end: string;
}

export interface Episode {
  id: string;
  title: string;
  /** Always null on the web: episode playback is app-only (the server
   * withholds audio URLs from crawler-readable responses). */
  audio_url: string | null;
  mime_type: string;
  start_date: string;
}

export interface EpisodeTrack {
  offset_seconds: number | null;
  name: string;
  artist: string;
  album: string;
}

/** The episode page's payload (`PublicEpisodeDetailSerializer`). Unlike the
 * other public pages this endpoint is shared with the apps, so it carries
 * more than the page renders. */
export interface EpisodeDetail extends Episode {
  /** The episode's own artwork only; empty when it has none. */
  image_url: string;
  /** DJ-entered notes in minimal markdown (bold/italic markers, newlines). */
  show_notes: string;
  duration_seconds: number | null;
  /** `slug` is empty for shows with no public page of their own. */
  show: { slug: string; name: string; image_url: string };
  tracks: EpisodeTrack[];
}

export interface ShowPageData {
  station: PublicStation;
  show: PublicShow;
  upcoming: Airing[];
  episodes: Episode[];
}

export interface DjPageData {
  station: PublicStation;
  dj: PublicDj;
  shows: ShowSummary[];
}

/** A show row in the directory: the summary plus what the cards render. */
export interface DirectoryShow extends ShowSummary {
  hosts: DjSummary[];
  /** May already be in progress (start in the past, end in the future) —
   * that's how a client tells the show is on the air right now. Null when
   * the programming grid doesn't know. */
  next_airing: Airing | null;
}

export interface DirectoryData {
  shows: DirectoryShow[];
  djs: DjSummary[];
}

export const directoryPath = (station: string) =>
  `station/${seg(station)}/public/directory`;

export const showPagePath = (station: string, show: string) =>
  `station/${seg(station)}/public/show/${seg(show)}`;

export const djPagePath = (station: string, dj: string) =>
  `station/${seg(station)}/public/dj/${seg(dj)}`;

// Not under public/ like the rest: the episode detail endpoint predates the
// episode page and is shared with the apps (it's in the OpenAPI schema).
export const episodePagePath = (station: string, episode: string) =>
  `station/${seg(station)}/episodes/${seg(episode)}`;

/** The DRF cursor envelope the episode archive pages through. */
export interface EpisodesPageData {
  results: Episode[];
  /** The next page's full URL, or null on the last page; the archive page
   * lifts the cursor param out of it. */
  next: string | null;
}

export const showEpisodesPath = (
  station: string,
  show: string,
  cursor?: string,
) =>
  `station/${seg(station)}/public/show/${seg(show)}/episodes${
    cursor ? `?cursor=${seg(cursor)}` : ""
  }`;

/**
 * Airings and episode dates render in the *viewer's* timezone — a listener
 * wants to know when a show starts where they are — so the zone is spelled
 * out to keep that unambiguous.
 */
export const formatAiring = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });

export const formatEpisodeDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
